import { useState } from 'react';
import { Clock, X } from 'lucide-react';
import { apiPost } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface Props {
  onCreated: () => void;
  onCancel: () => void;
  entityType?: string;
  entityId?: string;
}

const PRESETS = [
  { label: 'In 15 min', minutes: 15 },
  { label: 'In 1 hour', minutes: 60 },
  { label: 'In 4 hours', minutes: 240 },
  { label: 'Tomorrow 9 AM', minutes: -1 }, // sentinel: handled separately
  { label: 'Next Monday 9 AM', minutes: -2 },
];

function getPresetTime(minutes: number): string {
  if (minutes === -1) {
    // Tomorrow 9 AM
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  if (minutes === -2) {
    // Next Monday 9 AM
    const d = new Date();
    const daysUntilMonday = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMonday);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  return new Date(Date.now() + minutes * 60 * 1000).toISOString();
}

export function ReminderQuickCreate({ onCreated, onCancel, entityType, entityId }: Props) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const [creating, setCreating] = useState(false);

  async function handlePreset(minutes: number) {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    setCreating(true);
    try {
      await apiPost('/reminders', {
        title: title.trim(),
        remind_at: getPresetTime(minutes),
        entity_type: entityType ?? null,
        entity_id: entityId ?? null,
      });
      toast.success('Reminder created');
      onCreated();
    } catch { toast.error('Failed to create reminder'); }
    finally { setCreating(false); }
  }

  async function handleCustom() {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    if (!customDate) { toast.error('Pick a date'); return; }
    setCreating(true);
    try {
      const remindAt = new Date(`${customDate}T${customTime}:00`).toISOString();
      await apiPost('/reminders', {
        title: title.trim(),
        remind_at: remindAt,
        entity_type: entityType ?? null,
        entity_id: entityId ?? null,
      });
      toast.success('Reminder created');
      onCreated();
    } catch { toast.error('Failed to create reminder'); }
    finally { setCreating(false); }
  }

  return (
    <div className="bg-bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={16} className="text-accent" />
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What do you need to remember?"
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          autoFocus
          onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
        />
        <button onClick={onCancel} className="p-1 text-text-muted hover:text-text-primary"><X size={14} /></button>
      </div>

      <div className="flex gap-1 mb-3">
        <button
          onClick={() => setMode('preset')}
          className={`px-2 py-1 text-xs rounded ${mode === 'preset' ? 'bg-bg-hover text-text-primary' : 'text-text-muted'}`}
        >Quick</button>
        <button
          onClick={() => setMode('custom')}
          className={`px-2 py-1 text-xs rounded ${mode === 'custom' ? 'bg-bg-hover text-text-primary' : 'text-text-muted'}`}
        >Date/Time</button>
      </div>

      {mode === 'preset' ? (
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p.minutes)}
              disabled={creating}
              className="px-3 py-1.5 text-xs bg-bg-input border border-border rounded-md text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors disabled:opacity-50"
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customDate}
            onChange={e => setCustomDate(e.target.value)}
            className="px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary"
          />
          <input
            type="time"
            value={customTime}
            onChange={e => setCustomTime(e.target.value)}
            className="px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary"
          />
          <button
            onClick={handleCustom}
            disabled={creating}
            className="px-3 py-1.5 text-xs bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      )}
    </div>
  );
}
